"use client";

import Image from "next/image";
import type React from "react";
import { useEffect, useRef, useState } from "react";

interface VideoSectionProps {
  videoSrc: string;
  thumbnailSrc: string;
  studioName: string;
  projectTitle: string;
  className?: string;
  priority?: boolean;
}

const VideoSection: React.FC<VideoSectionProps> = ({
  videoSrc,
  thumbnailSrc,
  studioName,
  projectTitle,
  className = "",
  priority = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={`group relative w-full cursor-pointer overflow-hidden rounded-lg ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail Image */}
      <Image
        fill
        src={thumbnailSrc}
        alt={projectTitle}
        sizes="(min-width: 1024px) 50vw, 100vw"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${isHovered ? "opacity-0" : "opacity-100"
          }`}
        priority={priority}
      />

      {/* Video */}
      <video
        ref={videoRef}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${isHovered ? "opacity-100" : "opacity-0"
          }`}
        muted
        playsInline
        preload="metadata"
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="absolute inset-0 bg-black/30 transition-all duration-500 group-hover:bg-black/20" />

      <div className="absolute inset-0 bottom-6 flex items-end">
        <div className="px-4 text-left text-white sm:px-5 md:px-6 lg:px-8 transition-opacity duration-300 group-hover:opacity-60">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider opacity-80 sm:text-sm md:text-base">
            {studioName}
          </p>
          <h2 className="relative max-w-md text-2xl font-light tracking-tight sm:text-3xl md:text-4xl lg:max-w-lg">
            <span className="absolute -inset-x-1 -inset-y-0.5 bg-black/40 rounded blur-sm transition-opacity duration-300 group-hover:opacity-0" />
            <span className="relative">{projectTitle}</span>
          </h2>
        </div>
      </div>
    </div>
  );
};

const Demos: React.FC = () => {
  const videoSections = [
    {
      thumbnailSrc: "https://depot.moondream.ai/seg/crack-in-wall-cover2.jpeg",
      videoSrc: "https://depot.moondream.ai/seg/crack-in-wall-web.mp4",
      studioName: "Use Case",
      projectTitle: "Defect Tracking",
    },

    {
      thumbnailSrc: "https://depot.moondream.ai/seg/yellowing_leaves-cover2.jpeg",
      videoSrc: "https://depot.moondream.ai/seg/yellowing_leaves-web.mp4",
      studioName: "Use Case",
      projectTitle: "Agriculture",
    },
    {
      thumbnailSrc: "https://depot.moondream.ai/seg/laundry-on-floor-cover2.jpeg",
      videoSrc: "https://depot.moondream.ai/seg/laundry-on-floor-web.mp4",
      studioName: "Use Case",
      projectTitle: "Robotics",
    },
    {
      thumbnailSrc: "https://depot.moondream.ai/seg/coke-ads-cover2.jpeg",
      videoSrc: "https://depot.moondream.ai/seg/coke-ads-web.mp4",
      studioName: "Use Case",
      projectTitle: "Media",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
      {videoSections.map((section, index) => (
        <VideoSection
          key={index}
          videoSrc={section.videoSrc}
          thumbnailSrc={section.thumbnailSrc}
          studioName={section.studioName}
          projectTitle={section.projectTitle}
          className="aspect-video"
          priority={index === 0}
        />
      ))}
    </div>
  );
};

export { Demos };
